import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageViewService } from 'src/app/core/pageview.service';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { getRoutePath } from 'src/util/angular/ng-router-helpers';
import { race, Subject } from 'rxjs';
import { TickService } from 'src/app/shared/tick.service';
import { takeUntil } from 'rxjs/operators';
import { createSafeProps } from '../tcs-app-insights/app-insights-util';
import { AppInsightsService } from '../tcs-app-insights/app-insights-initializer.service';
import { NewRelicService } from '../tcs-new-relic/new-relic-initializer.service';

type RequestIdleCallbackHandle = (handle: number) => void;
type RequestIdleCallbackOptions = {
  timeout?: number;
};
type RequestIdleCallbackDeadline = {
  readonly didTimeout: boolean;
  timeRemaining: () => number;
};

declare global {
  interface Window {
    requestIdleCallback: (
      callback: (deadline: RequestIdleCallbackDeadline) => void,
      options?: RequestIdleCallbackOptions | undefined,
    ) => number;
    cancelIdleCallback: RequestIdleCallbackHandle;
  }
}

@Injectable({ providedIn: 'root' })
export class CpuIdleObserverService implements OnDestroy {
  private static initialized = false;
  private stopInterval$ = new Subject();
  private _onDestroy$ = new Subject();

  constructor(
    private _pageViews: PageViewService,
    private _activatedRoute: ActivatedRoute,
    private _log: Logger,
    private _tick: TickService,
    private _appInsightsService: AppInsightsService,
    private _newRelicService: NewRelicService,
  ) {}

  public init() {
    if (CpuIdleObserverService.initialized) {
      this._log.warning('CpuIdleObserverService.init: already initialized');
      return;
    }
    // try {
    //   this._initPageViewTracking();
    // } catch (err) {
    //   this._log.error(`CpuIdleObserverService_initPageViewTracking(): ${err}`);
    // }

    // try {
    //   this._initRegularCpuObservation();
    // } catch (err) {
    //   this._log.error(`CpuIdleObserverService._initRegularCpuObservation(): ${err}`);
    // }

    CpuIdleObserverService.initialized = true;
  }

  private _initPageViewTracking() {
    const sub = this._pageViews.pageviews$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((pageView) => {
        if (window.requestIdleCallback == null) {
          this._log.warning(
            'CpuIdleObserverService:_initPageViewTracking: browser does not support requestIdleCallback, not observing pageviews',
          );
          sub.unsubscribe();
          return;
        }
        window.requestIdleCallback(() => {
          try {
            const timeUntilIdleMs = Math.round(performance.now() - pageView.endTime);
            const pageResponsiveInMs = Math.round(timeUntilIdleMs + pageView.durationInMs);
            this._appInsightsService.appInsights?.trackEvent({
              name: 'cpuIdleAfterPageView',
              properties: createSafeProps({ pageView, timeUntilIdleMs, pageResponsiveInMs }),
            });
            this._newRelicService.newRelic?.addPageAction('cpuIdleAfterPageView', {
              ...createSafeProps({ pageView, timeUntilIdleMs, pageResponsiveInMs }),
            });
          } catch (err) {
            this._log.error(`CpuIdleObserverService pageViews$ requestIdleCallback: ${err}`);
          }
        });
      });
  }

  private _initRegularCpuObservation() {
    const destroy$ = race(this.stopInterval$, this._onDestroy$);
    this._tick.everyFiveSeconds$.pipe(takeUntil(destroy$)).subscribe(() => {
      if (window.requestIdleCallback == null) {
        this._log.warning(
          'CpuIdleObserverService:_initRegularCpuObservation: browser does not support requestIdleCallback, not observing timeUntilCpuIdle',
        );
        this.stopInterval$.next(null);
        this.stopInterval$.complete();
        return;
      }

      const beginTime = Math.round(performance.now());
      const beginUrl = window.location.href;
      const beginRoute = getRoutePath(this._activatedRoute.snapshot);

      window.requestIdleCallback(() => {
        try {
          const endTime = Math.round(performance.now());
          const endUrl = window.location.href;
          const timeUntilIdle = endTime - beginTime;
          const endRoute = getRoutePath(this._activatedRoute.snapshot);
          const props = {
            beginTime,
            beginUrl,
            beginRoute,
            endTime,
            endUrl,
            endRoute,
            timeUntilIdle,
          };
          this._appInsightsService.appInsights?.trackMetric({
            name: 'timeUntilCpuIdle',
            average: timeUntilIdle,
            properties: createSafeProps(props),
          });
          this._newRelicService.newRelic?.addPageAction('cpuIdleAfterPageView', {
            ...createSafeProps(props),
          });
        } catch (err) {
          this._log.error(
            `CpuIdleObserverService timeUntilCpuIdle interrval requestIdleCallback: ${err}`,
          );
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
